/* Shared styles of the small UI components in this folder. */

/* ------------------------------------------------------------ spinner & skeleton */
.spinner {
  display: inline-flex;
  color: currentColor;
}
.spinner svg { animation: spin 0.8s linear infinite; }

.skeleton {
  display: block;
  max-width: 100%;
  border-radius: var(--radius-sm);
  background: linear-gradient(90deg, var(--color-surface-2) 0%, var(--color-surface-3) 50%, var(--color-surface-2) 100%);
  background-size: 200% 100%;
  animation: shimmer 1.3s ease-in-out infinite;
}

.skeletonList { display: flex; flex-direction: column; gap: var(--space-2); }
.skeletonCard {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface);
}
.pageSkeleton { display: flex; flex-direction: column; gap: var(--space-3); padding: var(--space-5); max-width: 960px; }

@keyframes spin { to { transform: rotate(360deg); } }
@keyframes shimmer { from { background-position: 100% 0; } to { background-position: -100% 0; } }

/* ------------------------------------------------------------ empty / error states */
.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-2);
  padding: var(--space-7) var(--space-4);
  max-width: 480px;
  margin: 0 auto;
}
.stateCompact { padding: var(--space-5) var(--space-3); }
.stateIcon {
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--color-accent-soft);
  color: var(--color-accent);
  margin-bottom: var(--space-1);
}
.stateIcon svg { width: 26px; height: 26px; }
.stateCompact .stateIcon { width: 40px; height: 40px; }
.stateCompact .stateIcon svg { width: 20px; height: 20px; }
.stateIconDanger { background: var(--color-danger-soft); color: var(--color-danger); }
.stateTitle { font-weight: 650; font-size: var(--text-lg); }
.stateText { color: var(--color-text-muted); }
.stateAction { margin-top: var(--space-2); display: flex; gap: var(--space-2); flex-wrap: wrap; justify-content: center; }

.inlineError {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  color: var(--color-danger);
  font-size: var(--text-sm);
}
.inlineError svg { width: 16px; height: 16px; flex: none; margin-top: 2px; }

/* ------------------------------------------------------------ avatar */
.avatar {
  display: inline-grid;
  place-items: center;
  flex: none;
  border-radius: 50%;
  color: #fff;
  font-weight: 650;
  letter-spacing: 0.02em;
  line-height: 1;
  user-select: none;
}
.avatarStack { display: inline-flex; align-items: center; }
/* Small overlap only: the initials of every avatar stay readable. */
.avatarStack > * + * { margin-left: -3px; }
.avatarStack > * { box-shadow: 0 0 0 2px var(--color-surface); }
.avatarMore { background: var(--color-surface-3); color: var(--color-text-muted); }

/* ------------------------------------------------------------ search */
.search { position: relative; display: flex; align-items: center; min-width: 0; }
.searchIcon {
  position: absolute;
  left: 10px;
  width: 18px;
  height: 18px;
  color: var(--color-text-subtle);
  pointer-events: none;
}
.searchInput { padding-left: 36px; padding-right: 36px; }
.searchInput::-webkit-search-cancel-button { display: none; }
.searchClear {
  position: absolute;
  right: 4px;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
}
.searchClear svg { width: 16px; height: 16px; }
.searchClear:hover { background: var(--color-surface-2); }

/* ------------------------------------------------------------ tabs */
.tabs {
  display: flex;
  gap: 2px;
  overflow-x: auto;
  scrollbar-width: none;
  border-bottom: 1px solid var(--color-border);
}
.tabs::-webkit-scrollbar { display: none; }
.tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: var(--tap-target);
  padding: 0 var(--space-3);
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: var(--color-text-muted);
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}
.tab svg { width: 18px; height: 18px; }
.tab:hover { color: var(--color-text); }
.tabActive { color: var(--color-primary); border-bottom-color: var(--color-primary); }

/* ------------------------------------------------------------ toasts */
.toaster {
  position: fixed;
  z-index: 200;
  right: var(--space-4);
  bottom: calc(var(--space-4) + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  width: min(420px, calc(100vw - 2 * var(--space-4)));
  pointer-events: none;
}
@media (max-width: 767.98px) {
  /* Above the bottom tab bar on phones. */
  .toaster { left: var(--space-3); right: var(--space-3); width: auto; bottom: calc(var(--tabbar-height) + var(--space-3) + env(safe-area-inset-bottom)); }
}
.toastRegion { display: flex; flex-direction: column; gap: var(--space-2); }
.toast {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-2) var(--space-2) var(--space-3);
  border-radius: var(--radius);
  background: var(--color-text);
  color: var(--color-bg);
  box-shadow: var(--shadow-lg);
  pointer-events: auto;
  animation: toastIn 0.16s ease-out;
}
.toastIcon { width: 20px; height: 20px; flex: none; }
.toast_success .toastIcon { color: #5fd39a; }
.toast_warning .toastIcon { color: #f3b44c; }
.toast_error { background: var(--color-danger); color: var(--color-danger-contrast); }
.toast_info .toastIcon { color: var(--tl-cyan-500); }
.toastMessage { flex: 1 1 auto; min-width: 0; padding: 6px 0; }
.toastAction { background: transparent; color: inherit; border-color: currentColor; }
.toastAction:hover { background: rgba(255, 255, 255, 0.12); }
.toastClose {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: inherit;
  opacity: 0.75;
  cursor: pointer;
  flex: none;
}
.toastClose svg { width: 16px; height: 16px; }
.toastClose:hover { opacity: 1; background: rgba(255, 255, 255, 0.12); }
@keyframes toastIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }

/* ------------------------------------------------------------ resource picker */
.picker { display: flex; flex-direction: column; gap: var(--space-3); }
.pickerToggle { display: flex; align-items: center; gap: var(--space-2); }
.pickerGroup { display: flex; flex-direction: column; gap: 2px; }
.pickerGroupTitle {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: var(--space-1) 0;
  background: var(--color-surface);
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-muted);
}
.pickerList { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px; }
.pickerItem {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  min-height: var(--tap-target);
  padding: 4px var(--space-2);
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  text-align: left;
  cursor: pointer;
}
.pickerItem:hover:not(:disabled) { background: var(--color-surface-2); }
.pickerItem:disabled { opacity: 0.45; cursor: not-allowed; }
.pickerItemSelected { background: var(--color-accent-soft); }
.pickerCheck { width: 18px; height: 18px; color: var(--color-accent); flex: none; }
.pickerText { display: flex; flex: 1 1 auto; flex-direction: column; min-width: 0; }

/* "Extern" / "Geen account" next to a name (ResourceTag): the name truncates, the label stays whole. */
.resourceTag { flex: none; }

/* A check-row label with a hint line under it. */
.labelText { display: flex; flex-direction: column; min-width: 0; }
